import { flatten } from 'lodash';
import {
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDocs,
  query,
  serverTimestamp,
  where,
  writeBatch,
  orderBy,
} from 'firebase/firestore';
import { TagFromStore } from '@/types/types';
import { db } from '@/firebase/init';
import { getWorkspaceKeywordsPath, getWorkspaceTagsPath } from '@/utils/queryPaths';

export class TagsService {
  static async getAllTags(workspaceId: string | null, connectorId: string) {
    if (!workspaceId) return [];
    // throw { code: 'no-workspace-id', message: 'No workspace id provided' };
    const tags: TagFromStore[] = [];
    const snapshot = await getDocs(
      query(
        collection(db, getWorkspaceTagsPath(workspaceId, connectorId)),
        orderBy('name', 'asc'),
      ),
    );
    if (!snapshot) return [];
    snapshot.forEach((tag) => tags.push({ id: tag.id, ...tag.data() } as TagFromStore));
    return tags;
  }

  static async createTags(
    tagsInText: string,
    workspaceId: string,
    userId: string,
    connectorId: string,
  ) {
    const rawTags = flatten(tagsInText.split('\n').map((tag) => tag.split(',')));
    const tags = rawTags
      .map((tag) => tag.trim().toLowerCase())
      .filter((tag) => tag.length !== 0);

    const batch = writeBatch(db);

    tags.forEach((tag) => {
      const tagRef = doc(db, getWorkspaceTagsPath(workspaceId, connectorId), tag);
      batch.set(tagRef, {
        name: tag,
        createdAt: serverTimestamp(),
        createdBy: userId,
      });
    });

    await batch.commit();

    return null;
  }
  static async deleteTag(tagId: string, workspaceId: string, connectorId: string) {
    const tagsCount = await getCountFromServer(
      query(
        collection(db, getWorkspaceKeywordsPath(workspaceId, connectorId)),
        where('tags', 'array-contains', tagId),
      ),
    );
    if (tagsCount.data().count > 0) {
      throw new Error('Tag is in use');
    }
    await deleteDoc(doc(db, getWorkspaceTagsPath(workspaceId, connectorId), tagId));
    return null;
  }

  static async deleteTags(
    tableTagsIds: { id: string }[],
    workspaceId: string,
    connectorId: string,
  ) {
    const batch = writeBatch(db);
    await Promise.all(
      tableTagsIds.map(async (tag) => {
        const tagsCount = await getCountFromServer(
          query(
            collection(db, getWorkspaceKeywordsPath(workspaceId, connectorId)),
            where('tags', 'array-contains', tag.id),
          ),
        );
        if (tagsCount.data().count > 0) {
          throw new Error('Tag is in use');
        }
        batch.delete(doc(db, getWorkspaceTagsPath(workspaceId, connectorId), tag.id));
      }),
    );
    await batch.commit();
    return null;
  }
}
